<template>
  <div class="text-center z5">
    <v-dialog
      v-model="dialog"
      width="1400"
      content-class="sessionRecapCard"
      @click:outside="closeDialog"
    >
      <v-card width="1400">
        <v-card-title class="full-width">
          <span class="d-flex justify-center flex-grow-1">Entrée/Sortie</span>
          <v-icon size="28" color="third" class="justify-end" @click="closeDialog">mdi-close</v-icon>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-container>
            <v-form ref="form" v-model="formValid">
              <v-row>
                <v-col cols="4">
                  <DatePicker v-model="horseMovement.horse_movement_date" title="Date de la session *" icon="mdi-clock" />
                </v-col>
                <v-col cols="4">
                  <SelectPicker
                    v-model="horseMovement.horse_movement_type"
                    subtitle="Nature *"
                    :items="horseMovementTypes"
                    :rules="mandatoryRule"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <TextField
                    v-model="horseMovement.horse_movement_destination"
                    subtitle="Destination *"
                    :rules="mandatoryRule"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <TextArea
                    v-model="horseMovement.horse_movement_observation"
                    subtitle="Observation"
                    icon="mdi-message-text"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-row no-gutters>
          <v-col cols="12">
            <v-divider />
          </v-col>
        </v-row>
        <v-card-actions>
          <v-spacer />
          <Button
            label="Annuler"
            :action="closeDialog"
            icon="mdi-arrow-u-left-top"
          />
          <Button
            :disabled="!canCreateMovement"
            :label="buttonLabel"
            :action="createMovement"
            icon="mdi-content-save"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { getUnixTime, parseISO, format } from 'date-fns';
import { EventBus, sendNotification, mandatoryRule } from '../../../helpers/utils';
import { updateHorse } from '../../../helpers/request';
import { updateHorseOnStore, prepareHorse, deleteHorseMovementsFields } from '../../../helpers/dataManager';

import DatePicker from '../../common/Form/DatePicker.vue';
import SelectPicker from '../../common/Form/SelectPicker.vue';
import TextArea from '../../common/Form/TextArea.vue';
import TextField from '../../common/Form/TextField.vue';
import Button from '../../common/Button.vue';

import store from '@/store';

const toUtcMidnight = unixDate => getUnixTime(parseISO(`${format(new Date(unixDate * 1000), 'yyyy-MM-dd')}T00:00:00Z`));

export default {
  components: { DatePicker, SelectPicker, TextArea, TextField, Button },
  setup () {
    const dialog = ref(false);
    const horseMovement = ref({ horse_movement_type: null, horse_movement_date: null });
    const horse = ref(null);
    const horses = computed(() => store.state.horses);
    const references = computed(() => store.state.references);
    const formValid = ref(true);
    const editale = ref(false);

    const prepareMovement = movement => ({
      ...movement,
      horse_movement_date: parseInt(movement.horse_movement_date)
    });

    EventBus.$on('showHorseMovementModal', receivedObject => {
      dialog.value = true;
      horse.value = horses.value.find(h => h.uuid === receivedObject.horse_uuid);
      if (receivedObject.horse_movement) {
        // edit
        editale.value = true;
        horseMovement.value = prepareMovement(receivedObject.horse_movement);
      }
    });

    const buttonLabel = computed(() => (editale.value ? 'Modifier' : 'Créer'));

    const closeDialog = () => {
      dialog.value = false;
      horseMovement.value = { horse_movement_type: null, horse_movement_date: null };
    };

    const createMovement = async () => {
      const horseMovementArray = horse.value.horse_movements.map(movement => deleteHorseMovementsFields(movement));

      // if edit, remove it from array then add it with new values
      if (horseMovement.value.uuid) {
        const index = horseMovementArray.findIndex(m => m.uuid === horseMovement.value.uuid);

        if (index !== -1) {
          horseMovementArray.splice(index, 1);
        }
      }

      horseMovementArray.push({
        ...horseMovement.value,
        horse_movement_date: toUtcMidnight(horseMovement.value.horse_movement_date)
      });

      const updatedHorse = prepareHorse(horse.value, horseMovementArray, true);

      const response = await updateHorse(updatedHorse);

      if (!response.success) {
        const text = `Erreur lors de la ${editale.value ? 'édition' : 'création'}`;
        sendNotification('error', text);
        return;
      }
      const text = `Mouvement équestre ${editale.value ? 'édité' : 'créé'}`;
      updateHorseOnStore(updatedHorse, response.data[0].data, text);

      closeDialog();
    };

    const horseMovementTypes = computed(() => Object.keys(references.value.horseMovement).map(key => ({
      value: key,
      label: references.value.horseMovement[key]
    })));

    const canCreateMovement = computed(() => {
      if (!horseMovement.value.horse_movement_date) {
        return false;
      }

      if (!formValid.value) {
        return false;
      }

      return true;
    });

    return { canCreateMovement, formValid, closeDialog, dialog, horseMovement, createMovement, mandatoryRule, horseMovementTypes, buttonLabel };
  }
};

</script>
